/** Programmatic JSON-LD builders for SEO. Each returns a plain object that can be
 * passed (or combined into an array) to `useSEO({ jsonLd })`.
 */

const SITE_URL = (typeof window !== "undefined" && window.location.origin) || "https://waterfallslist.com";

function abs(path) {
  if (!path) return path;
  if (/^https?:/i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

function youtubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
  return m ? m[1] : null;
}

/** TouristAttraction (rich entity page) */
export function touristAttraction(wf, { reviews = [] } = {}) {
  const imgs = (wf.image_urls || []).map(abs).filter(Boolean);
  const obj = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    name: wf.name,
    description: wf.description || wf.meta_description || `Visit guide for ${wf.name}.`,
    url: abs(`/waterfalls/${wf.slug}`),
    image: imgs.length ? imgs : undefined,
    address: wf.address || `${wf.city || ""}, ${wf.state || ""}, ${wf.country || ""}`.replace(/^,\s*|,\s*$/g, ""),
    isAccessibleForFree: true,
    publicAccess: true,
    touristType: ["Nature lovers", "Hikers", "Photographers", "Families"],
    geo: wf.lat && wf.lng ? { "@type": "GeoCoordinates", latitude: wf.lat, longitude: wf.lng } : undefined,
    ...(wf.official_website ? { sameAs: [wf.official_website] } : {}),
    ...(wf.review_count > 0
      ? {
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: Number(wf.rating || 0).toFixed(1),
            reviewCount: wf.review_count,
            bestRating: 5,
            worstRating: 1,
          },
        }
      : {}),
    ...(reviews.length > 0 && {
      review: reviews.slice(0, 5).map((r) => ({
        "@type": "Review",
        reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
        author: { "@type": "Person", name: r.user_name || "Anonymous" },
        datePublished: r.created_at,
        reviewBody: r.text,
      })),
    }),
  };
  // Drop undefined keys
  Object.keys(obj).forEach((k) => obj[k] === undefined && delete obj[k]);
  return obj;
}

/** BreadcrumbList — `crumbs` is array of {name, path} */
export function breadcrumbList(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: abs(c.path),
    })),
  };
}

/** ItemList — for SEO category & listing pages */
export function itemList(name, items) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    numberOfItems: items.length,
    itemListElement: items.map((w, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: abs(`/waterfalls/${w.slug}`),
      name: w.name,
    })),
  };
}

/** FAQPage — array of {question, answer} */
export function faqPage(items) {
  if (!items || items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: { "@type": "Answer", text: q.answer },
    })),
  };
}

/** VideoObject — when the waterfall has a YouTube embed */
export function videoObject(wf) {
  const vid = youtubeId(wf.youtube_url);
  if (!vid) return null;
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: `${wf.name} — video`,
    description: `Video of ${wf.name} in ${wf.city || wf.state || wf.country}.`,
    thumbnailUrl: `https://i.ytimg.com/vi/${vid}/hqdefault.jpg`,
    embedUrl: `https://www.youtube.com/embed/${vid}`,
    contentUrl: `https://www.youtube.com/watch?v=${vid}`,
    uploadDate: wf.created_at || new Date().toISOString(),
  };
}

/** Place — supplemental for category landing pages (Country/State/City) */
export function placeForCategory({ name, kind, geo }) {
  return {
    "@context": "https://schema.org",
    "@type": kind === "country" ? "Country" : kind === "state" ? "AdministrativeArea" : "Place",
    name,
    ...(geo ? { geo: { "@type": "GeoCoordinates", ...geo } } : {}),
  };
}
